import { Alert, AlertTitle, Typography } from "@mui/material";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useDateFnsLocale } from "../../../utils/date-locale";
import useLocationHolidays from "../controller/use-location-holidays";
import {
	dateKeyToLocalDate,
	getBrowserTimeZone,
	getTimeZoneLabel,
	isDifferentWallClock,
} from "../utils/holiday-dates";

interface CreateShiftHolidayAlertProps {
	// Browser-local "YYYY-MM-DDTHH:mm" strings, as held by the Create Shift form
	startTime: string;
	endTime: string;
	locationId: number | string;
}

/**
 * Informational banner for Create Shift when the shift's start date at the
 * Location is a holiday. Never blocks posting.
 */
const CreateShiftHolidayAlert = ({
	startTime,
	endTime,
	locationId,
}: CreateShiftHolidayAlertProps) => {
	const { t, i18n } = useTranslation();
	const dateLocale = useDateFnsLocale();
	const numericLocationId = Number(locationId) || 0;
	const { getHolidaysAtInstant, getLocation } = useLocationHolidays(
		numericLocationId ? [numericLocationId] : []
	);

	const warning = useMemo(() => {
		const start = new Date(startTime);
		if (!numericLocationId || Number.isNaN(start.getTime())) return null;
		const { dateKey, timeZone, holidays } = getHolidaysAtInstant(
			numericLocationId,
			start
		);
		if (holidays.length === 0) return null;
		const end = new Date(endTime);
		const browserTimeZone = getBrowserTimeZone();
		return {
			date: format(dateKeyToLocalDate(dateKey), "EEEE, MMMM d, yyyy", {
				locale: dateLocale,
			}),
			names: holidays.map((holiday) => holiday.name).join(", "),
			timeZoneLabel: getTimeZoneLabel(timeZone, start, i18n.language),
			browserTimeZoneLabel: getTimeZoneLabel(browserTimeZone, start, i18n.language),
			isDifferentZone: isDifferentWallClock(timeZone, browserTimeZone, start),
			plantStart: formatInTimeZone(start, timeZone, "h:mm a"),
			plantEnd: Number.isNaN(end.getTime())
				? ""
				: formatInTimeZone(end, timeZone, "h:mm a"),
		};
	}, [startTime, endTime, numericLocationId, getHolidaysAtInstant, dateLocale, i18n.language]);

	if (!warning) return null;

	return (
		<Alert severity="warning" sx={{ mb: 2.5 }}>
			<AlertTitle>{t("holidays_create-warning-title")}</AlertTitle>
			{t("holidays_create-warning-body", {
				date: warning.date,
				name: warning.names,
				location: getLocation(numericLocationId)?.name ?? "",
				timeZone: warning.timeZoneLabel,
			})}
			{warning.isDifferentZone ? (
				<Typography sx={{ fontSize: "14px", mt: 0.5 }}>
					{t("holidays_create-warning-time-zone", {
						zone: warning.browserTimeZoneLabel,
						start: warning.plantStart,
						end: warning.plantEnd,
					})}
				</Typography>
			) : null}
		</Alert>
	);
};

export default CreateShiftHolidayAlert;
